import { frameworkInfos } from "@/lib/constants/frameworks";
import {
  buildDisclosureMap,
  buildDisclosuresByPillar,
  buildPillarCounts,
  getDisclosureValue,
} from "@/features/esg/components/reports/reportWizard/print/print-utils";
import { resolveCodeForFramework } from "@/features/esg/utils/exportHelpers";

const CATEGORY_ORDER = ["Environment", "Social", "Governance"];

const formatNumber = (value) => {
  if (value === null || value === undefined || Number.isNaN(value)) return "-";
  if (typeof value === "string") {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric.toLocaleString() : value;
  }
  return Number(value).toLocaleString();
};

const formatDate = (date) =>
  date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

const formatDateTime = (date) =>
  `${date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  })}, ${date.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  })}`;

const buildDefaultStrategy = (t, notes) => {
  const defaultStrategy = {
    overview: t(
      "This report summarizes our sustainability priorities and performance across environmental, social, and governance pillars.",
    ),
    environmental: [
      t("Reduce greenhouse gas emissions year over year."),
      t("Improve energy efficiency and renewable energy adoption."),
      t("Strengthen waste management and resource stewardship."),
    ],
    social: [
      t("Invest in employee wellbeing and safety."),
      t("Promote diversity, equity, and inclusion."),
      t("Support community engagement and development."),
    ],
    governance: [
      t("Enhance board oversight and accountability."),
      t("Strengthen risk management and compliance."),
      t("Promote ethical conduct across the organization."),
    ],
  };

  return {
    ...defaultStrategy,
    overview: notes || defaultStrategy.overview,
  };
};

const buildSummaryRows = (pillarCounts, progressPercentage) => {
  const totalDisclosures = CATEGORY_ORDER.reduce(
    (sum, pillar) => sum + (pillarCounts[pillar]?.total || 0),
    0,
  );
  const totalCompleted = CATEGORY_ORDER.reduce(
    (sum, pillar) => sum + (pillarCounts[pillar]?.completed || 0),
    0,
  );
  const completionPct =
    totalDisclosures > 0
      ? Math.round((totalCompleted / totalDisclosures) * 100)
      : progressPercentage || 0;

  return {
    totalDisclosures,
    totalCompleted,
    completionPct,
    summaryTableRows: CATEGORY_ORDER.map((pillar) => {
      const stats = pillarCounts[pillar] || {
        total: 0,
        completed: 0,
        percentage: 0,
      };
      return `<tr>
        <td>${pillar}</td>
        <td class="text-center">${stats.total}</td>
        <td class="text-center">${stats.completed}</td>
        <td class="text-center">${stats.percentage}%</td>
      </tr>`;
    }).join(""),
  };
};

const buildPillarTable = ({
  pillar,
  disclosuresByPillar,
  entriesByCode,
  pillarCounts,
  isDisclosureCompleted,
  exportFramework = "All",
  metricMappings = [],
  t,
}) => {
  const disclosures = disclosuresByPillar[pillar] || [];
  const rows = disclosures
    .map((disclosure) => {
      const resolved = resolveCodeForFramework(
        disclosure.code,
        disclosure,
        exportFramework,
        metricMappings,
      );
      if (!resolved) return null;

      const entries = entriesByCode.get(disclosure.code) || [];
      const { value, unit } = getDisclosureValue(entries, {
        entries: t("entries"),
        items: t("items"),
      });
      const done = isDisclosureCompleted?.(disclosure.code);
      return `<tr>
        <td>${resolved.code || ""}</td>
        <td>${resolved.title || ""}</td>
        <td class="text-right">${formatNumber(value)}</td>
        <td>${unit || ""}</td>
        <td class="text-center">${done ? t("Yes") : ""}</td>
      </tr>`;
    })
    .filter(Boolean)
    .join("");

  const stats = pillarCounts[pillar] || {
    total: disclosures.length,
    completed: 0,
  };

  return `
    <h3>${t(pillar)} ${t("Performance")}</h3>
    <table class="data-table">
      <thead>
        <tr>
          <th style="width: 14%;">${t("Code")}</th>
          <th style="width: 46%;">${t("Indicator")}</th>
          <th style="width: 16%;">${t("Value")}</th>
          <th style="width: 12%;">${t("Unit")}</th>
          <th style="width: 12%;">${t("Status")}</th>
        </tr>
      </thead>
      <tbody>
        ${rows || ""}
      </tbody>
    </table>
    <div class="summary-row">
      <div><strong>${t("Total")} ${t(pillar)} ${t(
        "Disclosures",
      )}:</strong> ${stats.total}</div>
      <div><strong>${t("Completed")}:</strong> ${stats.completed} ${t(
        "of",
      )} ${stats.total}</div>
    </div>
    <div class="page-break"></div>
  `;
};

export const buildWordReportHtml = ({
  reportName,
  framework,
  reportYear,
  orgName,
  notes,
  includeCover,
  includeToc,
  includeAppendix,
  availableDisclosures,
  customDisclosures,
  selectedCodesByCategory,
  entriesByCode,
  isDisclosureCompleted,
  progressPercentage,
  exportFramework = "All",
  metricMappings = [],
  t,
}) => {
  const now = new Date();
  const frameworkInfo =
    frameworkInfos.find((info) => info.key === framework) || frameworkInfos[0];
  const safeReportName = reportName || t("Custom Disclosure Pack");

  const disclosureMap = buildDisclosureMap([
    ...availableDisclosures,
    ...customDisclosures,
  ]);
  const disclosuresByPillar = buildDisclosuresByPillar(
    selectedCodesByCategory,
    disclosureMap,
  );
  const pillarCounts = buildPillarCounts(
    selectedCodesByCategory,
    isDisclosureCompleted,
  );

  const { totalDisclosures, totalCompleted, completionPct, summaryTableRows } =
    buildSummaryRows(pillarCounts, progressPercentage);
  const strategy = buildDefaultStrategy(t, notes);

  const coverSection = includeCover
    ? `
      <div class="cover">
        <div class="pill">${framework} ${t("Framework")}</div>
        <h1>${safeReportName}</h1>
        ${
          orgName
            ? `<p class="subtle" style="margin-top: 8px;">${t(
                "Organization",
              )}: ${orgName}</p>`
            : ""
        }
        <p class="subtle">${t("Reporting Year")}: ${reportYear}</p>
        <p class="subtle">${t("Generated on")} ${formatDate(now)}</p>
        <p class="subtle" style="margin-top: 24px; max-width: 360px;">
          ${t(
            "Prepared using data from our Environmental, Social, and Governance disclosure system.",
          )}
        </p>
      </div>
    `
    : "";

  const tocSection = includeToc
    ? `
      <div class="page page-break">
        <h2>${t("Table of Contents")}</h2>
        <table class="toc-table">
          <tr><td>1.</td><td>${t("Framework Overview")}</td></tr>
          <tr><td>2.</td><td>${t("Sustainability Strategy")}</td></tr>
          <tr><td>3.</td><td>${t("Summary of Disclosures by Pillar")}</td></tr>
          <tr><td>4.</td><td>${t("Environmental Performance")}</td></tr>
          <tr><td>5.</td><td>${t("Social Performance")}</td></tr>
          <tr><td>6.</td><td>${t("Governance Performance")}</td></tr>
          <tr><td>7.</td><td>${t("Appendix")}</td></tr>
        </table>
        <div class="note" style="margin-top: 18px;">
          ${t(
            "This report presents our sustainability performance across Environmental, Social, and Governance (ESG) dimensions.",
          )}
        </div>
      </div>
    `
    : "";

  const appendixSection = includeAppendix
    ? `
      <div class="page">
        <h2>${t("Appendix")}</h2>
        <h3>${t("Sample Indicator Tables")}</h3>
        <ul>
          <li><strong>${t("Code")}:</strong> ${t(
            "Unique disclosure identifier per framework",
          )}</li>
          <li><strong>${t("Indicator")}:</strong> ${t(
            "Full description of the disclosure requirement",
          )}</li>
          <li><strong>${t("Value")}:</strong> ${t(
            "Reported metric or data point",
          )}</li>
          <li><strong>${t("Unit")}:</strong> ${t(
            "Measurement unit (e.g., tCO2e, kWh, %, hours)",
          )}</li>
        </ul>

        <h3>${t("Methodology Notes")}</h3>
        <p><strong>${t("Data Collection")}:</strong> ${t(
          "All disclosed data is collected through our ESG management platform.",
        )}</p>
        <p><strong>${t("Reporting Period")}:</strong> ${t(
          "Unless otherwise specified, all metrics reflect annual performance for the reporting period stated on the cover page.",
        )}</p>
        <p><strong>${t("Completion Status")}:</strong> ${t(
          "Disclosures marked as completed have undergone internal review and include supporting documentation where applicable.",
        )}</p>

        <h3>${t("Glossary")}</h3>
        <p><strong>ESG:</strong> ${t(
          "Environmental, Social, and Governance - the three central factors used to measure sustainability and societal impact.",
        )}</p>
        <p><strong>${t("Disclosure")}:</strong> ${t(
          "A specific reporting requirement defined by the framework.",
        )}</p>
        <p><strong>tCO2e:</strong> ${t(
          "Metric tonnes of carbon dioxide equivalent.",
        )}</p>
        <p><strong>GHG:</strong> ${t(
          "Greenhouse gases - gases that trap heat in the atmosphere.",
        )}</p>
        <p><strong>${t("Materiality")}:</strong> ${t(
          "The principle of reporting on topics that have significant economic, environmental, and social impacts.",
        )}</p>

        <div class="note" style="margin-top: 14px;">
          <strong>${t("Note")}:</strong> ${t(
            "For questions regarding specific disclosures, data sources, or methodology, please contact your ESG reporting coordinator.",
          )}
        </div>

        <div class="footer">${t("End of Report - Generated on {{date}}", {
          date: formatDateTime(now),
        })}</div>
      </div>
    `
    : "";

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="UTF-8">
        <style>
          body { font-family: Arial, sans-serif; font-size: 10pt; color: #111827; line-height: 1.5; margin: 0; padding: 0; }
          h1 { font-size: 22pt; margin: 0 0 12px; }
          h2 { font-size: 16pt; margin: 0 0 10px; }
          h3 { font-size: 14pt; margin: 18px 0 8px; }
          p { margin: 6px 0; }
          .page { padding: 32px 36px; }
          .card { border: 1px solid #e5e7eb; border-radius: 10px; padding: 16px 18px; background: #fff; margin-bottom: 16px; }
          .toc-table, .data-table, .summary-table { width: 100%; border-collapse: collapse; }
          .toc-table th, .toc-table td { padding: 8px 6px; border-bottom: 1px solid #e5e7eb; font-size: 10pt; }
          .summary-table th, .summary-table td,
          .data-table th, .data-table td { border: 1px solid #e5e7eb; padding: 8px 10px; }
          .summary-table th, .data-table th { background: #f8fafc; text-align: left; font-weight: 600; }
          .text-right { text-align: right; }
          .text-center { text-align: center; }
          .pill { display: inline-block; padding: 6px 12px; border-radius: 999px; background: #0f9a6a; color: #fff; font-weight: 700; font-size: 9pt; }
          .subtle { color: #6b7280; font-size: 9pt; }
          .note { border: 1px solid #dbeafe; background: #eef2ff; padding: 12px 14px; border-radius: 8px; font-size: 10pt; color: #1e3a8a; }
          .summary-row { display: flex; justify-content: space-between; background: #f8fafc; border: 1px solid #e5e7eb; padding: 10px 12px; border-radius: 8px; margin: 6px 0 18px; }
          .page-break { page-break-after: always; }
          .cover { height: 90vh; display: flex; flex-direction: column; justify-content: center; align-items: center; text-align: center; background: linear-gradient(135deg, #e0f7e9 0%, #e8f2ff 100%); }
          .footer { text-align: center; font-size: 9pt; color: #6b7280; margin-top: 12px; }
        </style>
      </head>
      <body>
        ${coverSection}
        ${tocSection}
        <div class="page page-break">
          <h2>${t("Framework Overview")}</h2>
          <div class="card">
            <table class="summary-table">
              <tr>
                <th>${t("Framework")}</th>
                <th>${t("Report Name")}</th>
                <th>${t("Total Disclosures")}</th>
                <th>${t("Completion")}</th>
              </tr>
              <tr>
                <td>${framework}</td>
                <td>${safeReportName}</td>
                <td class="text-center">${totalDisclosures}</td>
                <td class="text-center">${completionPct}%</td>
              </tr>
            </table>
            ${orgName ? `<p><strong>${t("Organization")}:</strong> ${orgName}</p>` : ""}
            ${
              frameworkInfo?.summary
                ? `<p><strong>${t("Framework Description")}:</strong> ${
                    frameworkInfo.summary
                  }</p>`
                : ""
            }
          </div>
          <div class="note">
            <strong>${t("About this report")}:</strong> ${t(
              "This document provides a comprehensive view of ESG disclosures structured according to framework requirements.",
            )}
          </div>
        </div>

        <div class="page page-break">
          <h2>${t("Sustainability Strategy")}</h2>
          <div class="card">
            <p>${strategy.overview}</p>
          </div>
          <h3>${t("Environmental Focus")}</h3>
          <ul>
            ${strategy.environmental.map((s) => `<li>${s}</li>`).join("")}
          </ul>
          <h3>${t("Social Responsibility")}</h3>
          <ul>
            ${strategy.social.map((s) => `<li>${s}</li>`).join("")}
          </ul>
          <h3>${t("Governance Commitments")}</h3>
          <ul>
            ${strategy.governance.map((s) => `<li>${s}</li>`).join("")}
          </ul>
          <div class="note" style="margin-top: 12px;">
            <strong>${t("Our commitment")}:</strong> ${t(
              "These strategic priorities guide decision-making, risk management, and stakeholder engagement.",
            )}
          </div>
        </div>

        <div class="page page-break">
          <h2>${t("Summary of Disclosures by Pillar")}</h2>
          <table class="summary-table">
            <thead>
              <tr>
                <th>${t("Pillar")}</th>
                <th>${t("Total Disclosures")}</th>
                <th>${t("Completed")}</th>
                <th>${t("Completion Rate")}</th>
              </tr>
            </thead>
            <tbody>
              ${summaryTableRows}
              <tr>
                <td><strong>${t("Total")}</strong></td>
                <td class="text-center"><strong>${totalDisclosures}</strong></td>
                <td class="text-center"><strong>${totalCompleted}</strong></td>
                <td class="text-center"><strong>${completionPct}%</strong></td>
              </tr>
            </tbody>
          </table>
          <div class="note">
            ${t(
              "Completion rates indicate the percentage of disclosures filled with data and reviewed.",
            )}
          </div>
        </div>

        <div class="page page-break">
          ${buildPillarTable({
            pillar: "Environment",
            disclosuresByPillar,
            entriesByCode,
            pillarCounts,
            isDisclosureCompleted,
            exportFramework,
            metricMappings,
            t,
          })}
          ${buildPillarTable({
            pillar: "Social",
            disclosuresByPillar,
            entriesByCode,
            pillarCounts,
            isDisclosureCompleted,
            exportFramework,
            metricMappings,
            t,
          })}
          ${buildPillarTable({
            pillar: "Governance",
            disclosuresByPillar,
            entriesByCode,
            pillarCounts,
            isDisclosureCompleted,
            exportFramework,
            metricMappings,
            t,
          })}
        </div>

        ${appendixSection}
      </body>
    </html>
  `;
};
